import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ErrorBoundary, useError } from '@mrkt/features/Platform';
import { KeyboardDetectionContext } from '@spotify-internal/encore-creator-web';

import { LoadingIndicator } from '../../../shared/components/LoadingIndicator';
import { useBannerActions } from '../../../features/Banner';
import { usePageLocation } from '../../../features/page';

export const MAIN_CONTENT_ID = 's4a-page-main-content';

function BodyError() {
  const { showError, hide } = useBannerActions();
  const [, clearError] = useError();
  const location = usePageLocation();

  useEffect(() => {
    const id = 'body-error';

    showError('Something went wrong', {
      id,
      dismissOnRouteChange: false,
    });

    return () => {
      hide(id);
    };
  }, [showError, hide]);

  // clear error on location change
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [location, clearError]);

  return null;
}

const MainContent = styled.div<{ $isUsingKeyboard: boolean }>`
  ${({ $isUsingKeyboard }) =>
    !$isUsingKeyboard &&
    `
    &:focus {
      outline: 0;
    }
  `}
`;

type Props = {
  children: React.ReactNode;
};

export function PageRouteBody({ children }: Props) {
  const { isUsingKeyboard } = useContext(KeyboardDetectionContext);

  return (
    <React.Suspense fallback={<LoadingIndicator />}>
      <ErrorBoundary name="s4a-page-body" fallback={<BodyError />}>
        <MainContent
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          $isUsingKeyboard={isUsingKeyboard}
        >
          {children}
        </MainContent>
      </ErrorBoundary>
    </React.Suspense>
  );
}
